"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.findCrossings = findCrossings;
var _vector = require("./vector");
var _checkAngles = require("./checkAngles");
var _getPointTangent = require("./getPointTangent");
/**
 * Finds all crossing points of a closed contour with a line.
 * @note This method is resistant to cases where the line passes directly through the endpoints of two adjacent
 * entities. Due to limited numerical precision, naive implementations that search for intersections entity by entity
 * may report zero, one, or two intersections at that point.
 * @param contour The contour to find crossing points for. The contour should be closed. If it is not, a ghost crossing
 * point may be included, as if the endpoints of the contour were connected with a straight line segment.
 * @param lineEntity A line entity to find crossing points with. All crossing points on the infinite line will be
 * returned, even if they are beyond the endpoints of the line segment.
 * @returns An array of intersection points. The array will *always* contain an even number of points.
 */
function findCrossings(contour, line) {
  // Method: Traverse the contour, calculating the dot product of the ending point on each entity on the contour with the normal to the line.
  // When the dot product changes sign, a crossing has occurred on that entity.
  // While doing this, if an arc does not change sign, check for intersections with the line anyway. It's possible that the bulge of the arc
  // intersects the line. Add these intersections only if there are two of them.

  const crossingPoints = [];
  const tangent = (0, _vector.sub)(line.end, line.start);
  const normal = {
    x: -tangent.y,
    y: tangent.x
  };
  let lastPositive = null;
  let lastPt = null;
  // We repeat the first entity at the end of the loop, so that we can check for crossings between the last entity and the first entity.
  for (let _i = 0; _i <= contour.entities.length; _i++) {
    let i = _i % contour.entities.length;
    const entity = contour.entities[i];
    const thisPt = (0, _getPointTangent.getPointTangentOfEntity)(entity, entity.reversed ? 0 : 1)[0];
    const thisDot = (0, _vector.dot)((0, _vector.sub)(thisPt, line.start), normal);
    const thisPositive = thisDot > 0;
    if (lastPositive !== thisPositive && lastPositive !== null && lastPt !== null) {
      // TODO: Special handling for line segments that are co-linear with the line.

      // We have a crossing. Try to find the intersection point.
      let crossingPoint;
      let theseIsects;
      switch (entity.type) {
        case 'LINE':
          const lineLineIsect = intersectLineLine(entity, line);
          theseIsects = [{
            point: lineLineIsect.point,
            tEntity: lineLineIsect.t1,
            tLine: lineLineIsect.t2
          }];
          break;
        case 'ARC':
          theseIsects = intersectArcLine(entity, line);
          break;
        case 'CIRCLE':
          theseIsects = intersectArcLine({
            ...entity,
            type: 'ARC',
            startAngle: 0,
            endAngle: Math.PI * 2
          }, line);
          break;
        default:
          throw new Error(`Unsupported entity type: ${entity.type}`);
      }
      if (theseIsects.length === 1) {
        crossingPoint = {
          entity,
          entityIndex: i,
          ...theseIsects[0]
        };
      } else {
        // We were supposed to find exactly one intersection point. We must add an intersection point. Just interpolate
        // between the last point and this point, and add that.
        const backupEntity = {
          type: 'LINE',
          start: lastPt,
          end: thisPt
        };
        const lineLineIsect = intersectLineLine(backupEntity, line);
        crossingPoint = {
          entity,
          entityIndex: i,
          point: lineLineIsect.point,
          tEntity: lineLineIsect.t1,
          tLine: lineLineIsect.t2
        };
      }
      crossingPoints.push(crossingPoint);
    }
    if (lastPositive === thisPositive) {
      // If this entity is an arc, check for intersections with the line. Add them only if there are exactly two of them.
      let theseIsects;
      if (entity.type === 'ARC') {
        theseIsects = intersectArcLine(entity, line);
      } else if (entity.type === 'CIRCLE') {
        theseIsects = intersectArcLine({
          ...entity,
          type: 'ARC',
          startAngle: 0,
          endAngle: Math.PI * 2
        }, line);
      } else {
        theseIsects = [];
      }
      if (theseIsects.length === 2) {
        crossingPoints.push(...theseIsects.map(isect => ({
          entity,
          entityIndex: i,
          ...isect
        })));
      }
    }
    lastPositive = thisPositive;
    lastPt = thisPt;
  }
  return crossingPoints;
}

/**
 * Finds the intersection point between two lines.
 * See http://www.cs.swan.ac.uk/~cssimon/line_intersection.html
 * @param line1 The first line, defined by two points which the line passes through
 * @param line2 The second line, defined by two points which the line passes through
 * @returns A single point of intersection. It's possible for a points at infinity to be returned if the lines are parallel.
 */
function intersectLineLine(line1, line2) {
  const x1 = line1.start.x;
  const x2 = line1.end.x;
  const x3 = line2.start.x;
  const x4 = line2.end.x;
  const y1 = line1.start.y;
  const y2 = line1.end.y;
  const y3 = line2.start.y;
  const y4 = line2.end.y;
  const den = (x4 - x3) * (y1 - y2) - (x1 - x2) * (y4 - y3);
  const taNum = (y3 - y4) * (x1 - x3) + (x4 - x3) * (y1 - y3);
  const tbNum = (y1 - y2) * (x1 - x3) + (x2 - x1) * (y1 - y3);
  const ta = taNum / den;
  const tb = tbNum / den;
  return {
    point: {
      x: x1 + (x2 - x1) * ta,
      y: y1 + (y2 - y1) * ta
    },
    t1: ta,
    t2: tb
  };
}

/**
 * Finds the intersection point(s) between an arc and an infinite line.
 * @param arc The arc
 * @param line The line, defined by two points which the line passes through
 * @returns An array containing zero, one, or two points of intersection.
 */
function intersectArcLine(arc, line) {
  // Equation of line segment is p(t) = p1 + t * (p2 - p1)
  // Equation of the circle the arc came from is (x-q.x)^2 + (y-q.y)^2 = r^2
  // Find t that satisfies the circle equation (it will be a quadratic)

  const p1 = line.start;
  const p2 = line.end;
  const q = {
    x: arc.x,
    y: arc.y
  };
  const r = arc.r;

  // Circle equation becomes:
  // (p1.x - q.x + t * (p2.x - p1.x)) ^ 2 + (p1.y -q.y + t * (p2.y - p1.y)) ^ 2 = r^2

  // Then:
  // (A + t * B) ^ 2 + (C + t * D) ^ 2 = r ^ 2 
  // (A^2 + C^2) + (2*A*B + 2*C*D)*t + (B^2+D^2)*t^2 = r^2

  const A = p1.x - q.x;
  const B = p2.x - p1.x;
  const C = p1.y - q.y;
  const D = p2.y - p1.y;
  const a = B * B + D * D;
  const b = 2 * A * B + 2 * C * D;
  const c = A * A + C * C - r * r;

  // Then t are the solutions to the equation
  // a*t^2 + b*t + c = 0

  const disc = b * b - 4 * a * c;
  if (disc < 0) {
    return [];
  }
  let candidateTs;
  if (Math.abs(b) > 1e-12) {
    const d = -(b + Math.sign(b) * Math.sqrt(disc)) / 2;
    candidateTs = [d / a, c / d];
  } else {
    const d = Math.sqrt(disc);
    candidateTs = [(-b + d) / (2 * a), (-b - d) / (2 * a)];
  }
  let intersections = [];
  candidateTs.forEach(t => {
    // Intersects the circle
    const isect = (0, _vector.add)((0, _vector.mul)(p1, 1 - t), (0, _vector.mul)(p2, t));
    // Check the angle and compare to the arc

    let angleOfIsect = Math.atan2(isect.y - q.y, isect.x - q.x);
    // Now we can use startAngle and endAngle to know if the arc passes through angleOfIntersection 

    let startAngle = arc.startAngle;
    let endAngle = arc.endAngle;
    (0, _checkAngles.checkAngles)(arc);

    // Ensure startAngle < angleOfIsect < startAngle + 2 PI
    while (angleOfIsect < startAngle) angleOfIsect += Math.PI * 2; // angleOfIsect too small
    while (angleOfIsect > startAngle + Math.PI * 2) angleOfIsect -= Math.PI * 2; // angleOfIsect too big

    if (angleOfIsect <= endAngle) {
      intersections.push({
        point: isect,
        tEntity: (angleOfIsect - startAngle) / (endAngle - startAngle),
        tLine: t
      });
    }
  });
  return intersections;
}